import { useEffect, useState } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { useMutation } from "@tanstack/react-query";
import { getPartnersCredentials, resetSecretKey } from "api/landlord/landlord";
import { SuccessModal } from "./SuccessModal";
import { useSettingsController } from "../controller";
import { SettingsCard } from "./SettingsCard";
import { SettingsItem } from "./SettingsItem";
import { EditModal } from "./EditModal";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "shared/components/Loader";

export const SdkIntegration = () => {
  const { currentSettings, updateSettings } = useSettingsController();
  const settings = currentSettings.sdk;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showJwtSecret, setShowJwtSecret] = useState(false);

  const {
    data: resetSecretKeyData,
    mutate: mutateResetSecretKey,
    isPending: isResetSecretKeyLoading
  } = useMutation({
    mutationFn: () => {
      return resetSecretKey();
    },
    onSuccess: () => {
      setShowSuccessModal(true);
    }
  });

  const { data: partnersCredentials, isLoading: isPartnersCredentialsLoading } =
    useQuery({
      queryFn: getPartnersCredentials,
      queryKey: ["PARTNERS_CREDENTIALS"]
    });

  useEffect(() => {
    setAccessKey(settings.accessKey || "");
    setSecretKey(settings.secretKey || "");
  }, [settings.accessKey, settings.secretKey]);

  if (isPartnersCredentialsLoading)
    return (
      <SettingsCard title="SDK Integration">
        <Loader />
      </SettingsCard>
    );

  return (
    <>
      <SettingsCard title="SDK Integration" showButton={false}>
        <div className="space-y-4">
          <p className="text-sm font-medium text-content-200 pb-2 pt-6 border-t border-alpha-40">
            Access management
          </p>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <SettingsItem
                editMode={false}
                title="Access key"
                value={settings.accessKey || ""}
                onChange={setAccessKey}
              />
            </div>
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.SM}
              onClick={() => setShowEditModal(true)}
            >
              Edit
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <SettingsItem
                editMode={false}
                title="Secret key"
                value={secretKey}
                onChange={setSecretKey}
              />
            </div>

            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.SM}
              onClick={() => mutateResetSecretKey()}
              loading={isResetSecretKeyLoading}
            >
              Generate
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <SettingsItem
                editMode={false}
                title="JWT secret"
                value={
                  partnersCredentials?.partner.jwt_overriden_key
                    ? showJwtSecret
                      ? partnersCredentials.partner.jwt_overriden_key
                      : "*".repeat(
                          partnersCredentials.partner.jwt_overriden_key.length
                        )
                    : ""
                }
              />
              {partnersCredentials?.partner.jwt_overriden_key === null ? (
                <p className="text-sm text-danger-500">
                  Please contact Boom team to enable this feature.
                </p>
              ) : null}
            </div>

            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.SM}
              onClick={() => setShowJwtSecret((prev) => !prev)}
            >
              {showJwtSecret ? "Hide" : "Show"}
            </Button>
          </div>
        </div>
      </SettingsCard>
      <SuccessModal
        opened={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        secretKey={resetSecretKeyData?.partner.secret_key || ""}
      />
      <EditModal
        opened={showEditModal}
        onClose={() => setShowEditModal(false)}
        value={accessKey}
        onChange={setAccessKey}
        fieldName="access key"
        onSave={() => {
          updateSettings({ accessKey });
          setShowEditModal(false);
        }}
      />
    </>
  );
};
